// Tiny client for the fleetava server API (JSON over fetch).
// Plain module — no 'use client' needed. Only call from client components
// (or browser event handlers) since it uses `fetch`.

import type { Booking, Vehicle } from '@/types';

export class ApiError extends Error {
  status: number;
  body?: unknown;
  constructor(message: string, status: number, body?: unknown) {
    super(message);
    this.name = 'ApiError';
    this.status = status;
    this.body = body;
  }
}

type Init = Omit<RequestInit, 'body'> & { body?: string };

export async function api<T>(path: string, init?: Init): Promise<T> {
  const headers = new Headers(init?.headers);
  // Only a request with a body needs a JSON content type. Sending it on GET
  // adds an unnecessary header to every read.
  if (init?.body !== undefined && !headers.has('Content-Type')) {
    headers.set('Content-Type', 'application/json');
  }

  const res = await fetch(path, {
    ...init,
    headers,
  });
  const text = await res.text();
  let data: unknown = null;
  try {
    data = text ? (JSON.parse(text) as unknown) : null;
  } catch {
    data = text;
  }
  if (!res.ok) {
    const record = data !== null && typeof data === 'object' ? data as { error?: unknown; message?: unknown } : null;
    const msg =
      typeof record?.error === 'string'
        ? record.error
        : typeof record?.message === 'string'
          ? record.message
          : `Request failed (${res.status})`;
    throw new ApiError(msg, res.status, data);
  }
  return data as T;
}

export function get<T>(path: string): Promise<T> {
  return api<T>(path);
}

export function post<T>(path: string, body: unknown): Promise<T> {
  return api<T>(path, { method: 'POST', body: JSON.stringify(body) });
}

export function put<T>(path: string, body: unknown): Promise<T> {
  return api<T>(path, { method: 'PUT', body: JSON.stringify(body) });
}

export function patch<T>(path: string, body: unknown): Promise<T> {
  return api<T>(path, { method: 'PATCH', body: JSON.stringify(body) });
}

export function del<T>(path: string): Promise<T> {
  return api<T>(path, { method: 'DELETE' });
}

export function getVehicles(): Promise<Vehicle[]> {
  return get<Vehicle[]>('/api/vehicles');
}

export function createBooking(payload: Booking): Promise<Booking> {
  return post<Booking>('/api/bookings', payload);
}
